import { jobLists } from "character";
import FormComponents from "components/forms/FormComponents";
import Link from "components/link/Link";
import NoCharacter from "components/no-character/NoCharacter";
import { items } from "data-files/items";
import { quests } from "data-files/quests";
import { EorzeaDataType, GameData, InputType, LinkType, RelicData } from "enums";
import manager from "managers/app";
import { ViewRelicInfo } from "managers/types/relic-types";
import { Component } from "react";
import { connect } from "react-redux";
import t from "translations/translator";
import { AppState } from "types/app";
import ContentHeader from "views/page-parts/content/content-header/ContentHeader";
import DetailsSection from "views/page-parts/content/details-section/DetailsSection";
import Obtained from "views/page-parts/content/obtained/Obtained";
import OverviewTable from "views/page-parts/content/overview-table/OverviewTable";
import { Requirement } from "views/page-parts/content/relic-requirements/types";
import { ViewWeaponPhantomPenumbraeProps } from "views/relics/weapons/phantom/penumbrae/types";
import { TaskInfo } from "views/type";
import "./WeaponPhantomPenumbrae.css";

const mapStateToProps = (state:AppState):ViewWeaponPhantomPenumbraeProps.Redux => {
	return {
		lang: state.userInfo.options.lang,
		gameLang: state.userInfo.options.gameLang,
		userInfo: state.userInfo,
		hideCompletedSteps: state.userInfo.options.hideCompletedSteps,
		hideCompletedTasks: state.userInfo.options.hideCompletedTasks,
		showJob: state.userInfo.options.job,
	};
};

class WeaponPhantomPenumbrae extends Component<ViewWeaponPhantomPenumbraeProps.Component> {
	private requirements:Requirement[] = [];
	private importantNPCs:GameData.NPC[] = [];
	private relicInfo:ViewRelicInfo = {
		jobs: jobLists.relic[RelicData.Group.WEAPON_PHANTOM],
		type: RelicData.Type.WEAPON,
		relic: RelicData.Group.WEAPON_PHANTOM,
		part: RelicData.Part.WEAPON_PENUMBRAE,
		steps: [RelicData.Step.DEMIATMA, RelicData.Step.ARCANITE],
		stepPlaceholders: {
			[RelicData.Step.DEMIATMA]: { ITEM: "Demiatma" },
			[RelicData.Step.ARCANITE]: { ITEM: GameData.Item.ARCANITE },
		},
		next: null,
		previous: null,
	};

	constructor(props:ViewWeaponPhantomPenumbraeProps.Component){
		super(props);
		this.requirements = this.getRequirements();
		this.importantNPCs = this.getImportantNPCs();
	}

	render():JSX.Element{
		const character = manager.data.getActiveCharacter();
		if(character === null){ return <NoCharacter />; }

		const completeSteps = manager.content.getCompleteSteps(character.progress, this.relicInfo);

		const demiAtmaTaskInfo:TaskInfo[] = [
			// Note: Job doesn't matter in getTaskItem (all jobs need the same item)
			{ task: RelicData.Task.TASK_1, target: manager.relics.getTaskItem(RelicData.Step.DEMIATMA, RelicData.Task.TASK_1) },
			{ task: RelicData.Task.TASK_2, target: manager.relics.getTaskItem(RelicData.Step.DEMIATMA, RelicData.Task.TASK_2) },
			{ task: RelicData.Task.TASK_3, target: manager.relics.getTaskItem(RelicData.Step.DEMIATMA, RelicData.Task.TASK_3) },
			{ task: RelicData.Task.TASK_4, target: manager.relics.getTaskItem(RelicData.Step.DEMIATMA, RelicData.Task.TASK_4) },
			{ task: RelicData.Task.TASK_5, target: manager.relics.getTaskItem(RelicData.Step.DEMIATMA, RelicData.Task.TASK_5) },
			{ task: RelicData.Task.TASK_6, target: manager.relics.getTaskItem(RelicData.Step.DEMIATMA, RelicData.Task.TASK_6) },
		];

		const arcaniteStepInventory = manager.data.getInventoryStatus({
			item: manager.relics.getTaskItem(RelicData.Step.ARCANITE, RelicData.Task.TASK_1),
			relic: [this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, RelicData.Step.ARCANITE, RelicData.Task.TASK_1],
		});

		return (
			<div id="weapon-phantom-penumbrae" className="relic">
				<ContentHeader requirements={this.requirements} importantNPCs={this.importantNPCs} relicInfo={this.relicInfo}>
				</ContentHeader>
				<div id="relic-checklist">
					<OverviewTable character={character} completeSteps={completeSteps} relicInfo={this.relicInfo} />
				</div>
				<div id="relic-details">
					<h1>{t.t("Details")}</h1>
					{this.props.hideCompletedSteps && completeSteps.allComplete ? t.t("All Steps Complete") : null }
					<DetailsSection title={RelicData.Step.DEMIATMA} placeholders={this.relicInfo.stepPlaceholders[RelicData.Step.DEMIATMA]} id={RelicData.Step.DEMIATMA} stepComplete={completeSteps[RelicData.Step.DEMIATMA]}>
						<p>
							{t.t("Complete")} <Link type={LinkType.LODESTONE} lodestoneType={EorzeaDataType.BLUEQUEST} text={GameData.Quest.ARCANE_ARTISTRY} lodestoneID={quests[GameData.Quest.ARCANE_ARTISTRY].lodestoneID} />
						</p>
						<div className="table-wrapper">
							<table className="table">
								<thead><tr><th>{t.t("Obtained")}</th><th>{t.t("Item")}</th><th>{t.t("Quantity")}</th><th>{t.t("Inventory")}</th><th>{t.t("To Obtain")}</th></tr></thead>
								<tbody>
									{demiAtmaTaskInfo.map((thisTask, index) => {
										const target = items[thisTask.target as GameData.Item];
										const inventoryStatus = manager.data.getInventoryStatus({
											item: thisTask.target as GameData.Item,
											relic: [this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, RelicData.Step.DEMIATMA, thisTask.task],
										});

										let qtyToObtain = inventoryStatus.qtyPerJob - inventoryStatus.inventory;
										if(qtyToObtain < 0){ qtyToObtain = 0; }

										/*
											qtyNeeded is only used for recipes, which doesn't apply here but at the time of writeing was required
											REVIEW: Once Obtained component updated remove this
										*/
										return <tr key={`${RelicData.Step.DEMIATMA}-${index}`}>
											<td><Obtained obtained={target.obtained} qtyNeeded={inventoryStatus.qtyPerJob} /></td>
											<td>{t.t(target.name)}</td>
											<td>{inventoryStatus.qtyPerJob}</td>
											<td>
												<FormComponents.TextInput
													label={{ hide: true }}
													input={{ type: InputType.NUMBER, value: inventoryStatus.inventory, dataAttributes: { item: thisTask.target }, events: { onChange: manager.content.inventoryUpdated }}}
												/>
											</td>
											<td>{qtyToObtain}</td>
										</tr>;
									})}
									{
										character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, RelicData.Step.DEMIATMA)
											? <tr className="all-complete"><td colSpan={5}><p>{t.t("All Complete")}</p></td></tr>
											: null
									}
								</tbody>
							</table>
						</div>
					</DetailsSection>
					<DetailsSection title={RelicData.Step.ARCANITE} placeholders={this.relicInfo.stepPlaceholders[RelicData.Step.ARCANITE]} id={RelicData.Step.ARCANITE} stepComplete={completeSteps[RelicData.Step.ARCANITE]}>
						<p>{t.t("Complete")} <Link
							type={LinkType.LODESTONE}
							lodestoneType={EorzeaDataType.BLUEQUEST}
							text={GameData.Quest.FORGING_PHANTASMAL}
							lodestoneID={quests[GameData.Quest.FORGING_PHANTASMAL].lodestoneID}
						/></p>
						<div className="table-wrapper">
							<table className="table">
								<thead>
									<tr>
										<th>{t.t("Obtained")}</th>
										<th>{t.t("Item")}</th>
										<th>{t.t("Quantity / Job")}</th>
										<th>{t.t("Total")}</th>
										<th>{t.t("Used")}</th>
										<th>{t.t("Remaining")}</th>
										<th>{t.t("Inventory")}</th>
										<th>{t.t("To Obtain")}</th>
									</tr>
								</thead>
								<tbody>
									{this.props.hideCompletedTasks && arcaniteStepInventory.toObtain === 0
										? null
										: <tr className="odd-row">
											<td><Obtained obtained={items[GameData.Item.ARCANITE].obtained} qtyNeeded={arcaniteStepInventory.total} /></td>
											<td>{t.t(items[GameData.Item.ARCANITE].name)}</td>
											<td>{arcaniteStepInventory.qtyPerJob.toLocaleString()}</td>
											<td>{arcaniteStepInventory.total.toLocaleString()}</td>
											<td>{arcaniteStepInventory.used.toLocaleString()}</td>
											<td>{arcaniteStepInventory.remaining.toLocaleString()}</td>
											<td>
												<FormComponents.TextInput
													label={{ hide: true }}
													input={{
														type: InputType.NUMBER,
														value: arcaniteStepInventory.inventory,
														dataAttributes: { item: GameData.Item.ARCANITE },
														events: { onChange: manager.content.inventoryUpdated },
													}}
												/>
											</td>
											<td>{arcaniteStepInventory.toObtain.toLocaleString()}</td>
										</tr>
									}
									{
										character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, RelicData.Step.ARCANITE)
											? <tr className="all-complete"><td colSpan={8}><p>{t.t("All Complete")}</p></td></tr>
											: null
									}
								</tbody>
							</table>
						</div>
					</DetailsSection>
				</div>
			</div>
		);
	}

	componentDidMount():void{
		manager.view.changeComplete();
	}

	private getRequirements():Requirement[]{
		if(!this.relicInfo){ return []; }
		return [
			{ type: EorzeaDataType.MSQ, text: GameData.Quest.DAWNTRAIL, lodestoneID: quests[GameData.Quest.DAWNTRAIL].lodestoneID },
			{ type: EorzeaDataType.BLUEQUEST, text: GameData.Quest.UNFAMILIAR_TERRITORY, lodestoneID: quests[GameData.Quest.UNFAMILIAR_TERRITORY].lodestoneID },
		];
	}

	private getImportantNPCs():GameData.NPC[]{
		return [];
	}
}
export default connect(mapStateToProps)(WeaponPhantomPenumbrae);
